import React, { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { PaletteVariants } from "@/components/PaletteVariants";
import { PickCard, PickHint } from "@/components/PickCard";

/** معاينة مصغّرة مجانية لتخطيطات بطاقة العمل الثلاثة ببيانات العميل — يختار التخطيط ولوحة الألوان قبل الدفع (design_inputs._layout / _palette) */
export const CardLayoutPicker = ({ projectId, brand, inputs, onPick, onPalette }) => {
  const [list, setList] = useState([]);
  const [palette, setPalette] = useState([]);
  const [busy, setBusy] = useState(false);
  const [recoloring, setRecoloring] = useState(false);
  const [notice, setNotice] = useState("");
  const palName = useRef("");
  const prevPal = useRef(inputs?._palette || "");
  const timer = useRef(null);
  const chosen = inputs?._layout || "";
  const { _layout, _palette, ...rest } = inputs || {};
  const sig = JSON.stringify(rest) + (brand || "") + (_palette || "");
  useEffect(() => {
    clearTimeout(timer.current);
    const palChanged = prevPal.current !== (_palette || "");
    prevPal.current = _palette || "";
    if (palChanged) setRecoloring(true);
    timer.current = setTimeout(async () => {
      setBusy(true);
      const t0 = Date.now();
      try {
        const { data } = await api.post("/content/card-layouts", { project_id: projectId, brand_name: brand || "", design_inputs: rest, palette: _palette ? _palette.split(",").map((s) => s.trim()) : null });
        if (palChanged) await new Promise((r) => setTimeout(r, Math.max(0, 1200 - (Date.now() - t0))));  // يُرى مؤشر التغيير بوضوح
        setList(data.layouts || []);
        setPalette(data.palette || []);
        if (palChanged && _palette) { setNotice(`تم تلوين التخطيط بلوحة «${palName.current || "المختارة"}»`); setTimeout(() => setNotice(""), 5000); }
      } catch { /* المعاينة اختيارية */ } finally { setBusy(false); setRecoloring(false); }
    }, palChanged ? 50 : 700);
    return () => clearTimeout(timer.current);
  }, [sig, projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const basePalette = inputs?._base_palette ? inputs._base_palette.split(",").map((s) => s.trim()) : palette;
  return (
    <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="card-layouts">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs"><b className="text-primary">اختر تخطيط البطاقة وألوانها قبل التوليد:</b> معاينة مصغّرة ببياناتك وألوان شعارك — مجانًا. الفن النهائي يولَّد بالذكاء بنفس اللوحة ويُركَّب على التخطيط الذي تختاره.</p>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" data-testid="card-layouts-loading" />}
      </div>
      {list.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {list.map((l) => {
            const active = chosen === l.key;
            return (
              <PickCard key={l.key} active={active} busy={recoloring} notice={notice} onClick={() => onPick(active ? "" : l.key)} testid={`card-layout-${l.key}`} image={l.image} imageClass="aspect-[1.75]" imageAlt={l.label}>
                <div className="text-xs font-bold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-muted-foreground" />{l.label}</div>
                <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">{l.desc}</p>
              </PickCard>
            );
          })}
        </div>
      )}
      <PickHint show={list.length > 0 && !chosen} text="اختر تخطيطًا واحدًا من الثلاثة بالضغط عليه — تظهر عليه علامة «المختار» ثم جرّب لوحات الألوان." testid="card-layouts-pick-hint" />
      {basePalette?.length > 0 && onPalette && (
        <PaletteVariants base={basePalette} selected={palette} busy={busy} testid="card-palette-variants"
          onPick={(cols, nm) => { palName.current = nm || ""; onPalette(cols.join(", "), basePalette.join(", ")); }} />
      )}
      <p className="text-[11px] text-muted-foreground" data-testid="card-layout-chosen">
        {chosen ? <>التخطيط المختار: <b className="text-emerald-700 dark:text-emerald-400">{list.find((l) => l.key === chosen)?.label || chosen}</b> — اضغطه مرة أخرى لإلغاء الاختيار.</> : "لم تختر تخطيطًا — سنختار لك تلقائيًا (الحديث عند إرفاق صورة مشابهة)."}
        {_palette && <> · اللوحة المختارة: <span dir="ltr" className="font-mono">{_palette}</span></>}
      </p>
    </div>
  );
};
